import { Alert, Box, Button, CircularProgress, IconButton, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { RequestError, useApi } from "../../services";
import { useUserContext } from "../../context";
import { EditCompanieModal, ErrorModal } from "../modals";
import { Add, Edit, OpenInNew, Phone } from "@mui/icons-material";
import { CompanyData } from "../../pages";
import { formatBrazilianPhone, useWindowSize } from "../../core";
import { useNavigate } from "react-router-dom";

export interface TableCompaniesProps {
  title?: string;
  isAddNew?: boolean;
  filter?: boolean;
  onSearch?: (company: CompanyData) => void;
}

export const TableCompanies: React.FC<TableCompaniesProps> = ({ title, isAddNew, filter, onSearch }) => {
  const api = useApi();
  const user = useUserContext();
  const { height, width } = useWindowSize();

  const sizeRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const timerFilter = useRef<NodeJS.Timer>();

  const [initialCompany, setInitialCompany] = useState<CompanyData>();
  const [state, setState] = useState(false);
  const [errorAdd, setErrorAdd] = useState<RequestError>();
  const [openNewModal, setOpenNewModal] = useState(false);
  const [companies, setCompanies] = useState<string[] | RequestError>([]);
  const [nameFilter, setNameFilter] = useState("");
  const [totalItems, setTotalItems] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(100);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // Lista de Contratos
  useEffect(() => {
    const getContratos = async () => {
      if (!user || "error" in user) return;
      const data = await api.company.getAll(nameFilter ? nameFilter : " ", page, perPage);
      if ("error" in data) {
        setCompanies(data);
        return;
      }
      setTotalItems(data.totalItems);
      setCompanies(data.items);
    };

    if (timerFilter.current) clearTimeout(timerFilter.current);

    timerFilter.current = setTimeout(() => {
      getContratos();
    }, 100);

  }, [nameFilter, page, perPage, user, state]);

  useEffect(() => {
    setTimeout(() => {
      let heightTable = height;
      let widthTable = width;
      if (sizeRef.current) {
        const size = sizeRef.current.getBoundingClientRect();
        widthTable = size.width;
        heightTable = size.height;
      }
      if (divRef.current) {
        const size = divRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (filterRef.current) {
        const size = filterRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (titleRef.current) {
        const size = titleRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      setSize({ width: widthTable, height: heightTable });
    }, 100);

  }, [sizeRef, divRef, filterRef, titleRef, height, width]);

  const handleEdit = async (value?: CompanyData) => {
    if (!value) return;

    setOpenNewModal(false);
    setInitialCompany(undefined);
    setState(!state);
  };

  if ("error" in companies) {
    return <ErrorModal {...companies} />;
  }

  return <>
    {/* Erro */}
    {errorAdd &&
      <Snackbar
        open={Boolean(errorAdd)}
        autoHideDuration={4500}
        onClose={() => setErrorAdd(undefined)}
        message="Note archived"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorAdd(undefined)} severity="error" sx={{ width: "100%" }}>
          {errorAdd.error + ":" + errorAdd.message}
        </Alert>
      </Snackbar>
    }

    {/* Modal para o novo contrato */}
    {openNewModal &&
      <EditCompanieModal
        open={openNewModal}
        showCNPJ
        showSede
        showPhone
        showMunicipio
        initialCompany={initialCompany}
        onCompany={handleEdit}
        onCancel={() => {
          setOpenNewModal(false);
          setInitialCompany(undefined);
        }}
      />
    }

    {/* Tabela com os dados */}
    <Box
      ref={sizeRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Título e novo empreendimento */}
      <Box
        ref={titleRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography fontWeight={"bold"} fontSize={24}>
          {title ?? "Empreendimentos"}
        </Typography>

        {filter && <TextField sx={{ width: "356px" }} label="Nome" variant="standard"
          value={nameFilter}
          onChange={(ev) => setNameFilter(ev.target.value)}
        />}

        {isAddNew && user && !("error" in user) && user.admin &&
          <Button
            variant="contained"
            onClick={() => setOpenNewModal(true)}
            endIcon={<Add />}
          >
            Novo Empreendimento
          </Button>
        }
      </Box>

      {/* Tabela com os contratos */}
      <TableContainer sx={{ flexGrow: 1, width: size.width, height: size.height, overflow: "auto" }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {onSearch && <TableCell align="center" sx={{ fontWeight: "bold" }}>Selecionar</TableCell>}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Logo</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Nome</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Telefone Editar Acessar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map(id => <RowWaiting
              key={id}
              id={id}
              onEdit={(company) => {
                setInitialCompany(company);
                setOpenNewModal(true);
              }}
              onSearch={onSearch}
            />)}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalItems}
          rowsPerPage={perPage}
          page={page - 1}
          onPageChange={(ev, page) => setPage(page + 1)}
          onRowsPerPageChange={(ev) => setPerPage(parseInt(ev.target.value, 10))}
        />
      </TableContainer>
    </Box>
  </>;
};

const RowWaiting: React.FC<{
  id: string;
  onEdit: (company: CompanyData) => void;
  onSearch?: (company: CompanyData) => void;
}> = ({ id, onSearch, onEdit }) => {
  const api = useApi();
  const navigate = useNavigate();

  const [company, setCompany] = useState<CompanyData | RequestError>();

  // Dados do contrato
  useEffect(() => {
    api.company.company(id).then(data => setCompany(data));
  }, []);


  if (!company) return <TableRow>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
  </TableRow>;

  if ("error" in company) return <TableRow>
    <TableCell>{company.error}</TableCell>
    <TableCell>{company.message}</TableCell>
  </TableRow>;

  return <TableRow hover>
    {onSearch && <TableCell align="center">
      <IconButton onClick={() => onSearch(company)}>
        <OpenInNew />
      </IconButton>
    </TableCell>}

    {/* Logo */}
    <TableCell align="center">
      <img src={company.logo}
        alt={"logo"}
        style={{
          objectFit: "contain",
          height: `3em`,
          width: `3em`,
        }} />
    </TableCell>

    {/* Nome */}
    <TableCell align="center">{company.nome}</TableCell>

    {/* Phone, Editar, Acessar */}
    <TableCell align="center" sx={{ width: "250px" }}>

      <Tooltip title={formatBrazilianPhone(company.phone)}>
        <IconButton size="small">
          <Phone fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title='Editar'>
        <IconButton size="small" onClick={() => onEdit(company)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title='Ir para o empreendimento'>
        <IconButton size="small" onClick={() => navigate(`/app/home/${id}`)}>
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>
    </TableCell>

  </TableRow>;
};